import React from 'react';
import PropTypes from 'prop-types';
import i18next from 'i18next';
import Background from 'components/Background';
import CardOverlay from 'components/CardOverlay';
import Link from 'components/Link';
import trimText from 'lib/trimText';
import { hasTeaseImage, getTeaseUrl } from 'lib/teaseUtils';
import { getAimsUrlFlavor } from 'lib/getAimsUrl';

import contentPropType from 'lib/CustomPropTypes/content';

import cx from 'classnames';

// TODO: Update this image map to urls without hashes when the CMS supports that functionality
const FALLBACK_IMG_URL_MAP = {
  better: 'https://media4.s-nbcnews.com/i/newscms/2019_23/2887891/2x3_better_fallback_image_75962cb529381dae91e4953bf772b192.jpg',
  globalcitizen: 'https://media3.s-nbcnews.com/i/newscms/2019_23/2887896/2x3_global-citizen_fallback_image_75962cb529381dae91e4953bf772b192.jpg',
  knowyourvalue: 'https://media1.s-nbcnews.com/i/newscms/2019_23/2887901/2x3_know-your-value_fallback_image_75962cb529381dae91e4953bf772b192.jpg',
  leftfield: 'https://media3.s-nbcnews.com/i/newscms/2019_23/2887906/2x3_left-field_fallback_image_75962cb529381dae91e4953bf772b192.jpg',
  mach: 'https://media2.s-nbcnews.com/i/newscms/2019_23/2887911/2x3_mach_fallback_image_75962cb529381dae91e4953bf772b192.jpg',
  msnbc: 'https://media1.s-nbcnews.com/i/newscms/2019_23/2887916/2x3_msnbc_fallback_image_75962cb529381dae91e4953bf772b192.jpg',
  news: 'https://media4.s-nbcnews.com/i/newscms/2019_23/2887921/2x3_nbcnews_fallback_image_75962cb529381dae91e4953bf772b192.jpg',
  think: 'https://media1.s-nbcnews.com/i/newscms/2019_23/2887926/2x3_think_fallback_image_d1b83678453221b54caeeaba1d24d1b5.jpg',
  today: 'https://media2.s-nbcnews.com/i/newscms/2019_23/2887931/2x3_today_fallback_image_75962cb529381dae91e4953bf772b192.jpg',
};

function getBackgroundPicture(computedValues, vertical = 'news') {
  if (!hasTeaseImage(computedValues)) {
    Object.assign(computedValues, {
      alternateTeaseImages: [{
        aspectRatio: 'ASPECT_2X1',
        image: {
          altText: vertical,
          url: {
            primary: FALLBACK_IMG_URL_MAP[vertical.toLowerCase()],
          },
        },
      }],
    });
  }

  const teaseUrl = getTeaseUrl({ computedValues });
  return getAimsUrlFlavor(teaseUrl, { width: 240, height: 360 });
}

const title = (headline) => (
  <div data-test="title" className="bacon-cards-twobythree__title">
    {trimText(headline, 45)}
  </div>
);

const description = (dek) => (
  <div className="bacon-cards-twobythree__description" data-test="dek">
    {trimText(dek, 220)}
  </div>
);

const button = (url) => (
  <Link data-test="button" href={url} className="bacon-cards-twobythree__button">
    {i18next.t('VIEW')}
  </Link>
);

const TwoByThree = ({ card }, { vertical }) => {
  const {
    computedValues: {
      dek,
      headline,
      url,
      id,
    } = {},
  } = card;

  const cardOverlayContainer = cx(
    { 'bacon-cards-twobythree__overlay': !!(dek && dek.length) },
  );

  const onlySpaces = /^\s+$/;
  const noDek = !dek || onlySpaces.test(dek);
  const hedAndDekAreEmpty = noDek && onlySpaces.test(headline);

  return (
    <div
      className="bacon-cards-twobythree__container"
      data-test="container"
      data-contentid={id}
    >
      {headline
        && (
        <div className="bacon-cards-twobythree__title-wrapper">
          <Link href={url} className="bacon-cards-twobythree__title-link">
            {title(headline, vertical)}
          </Link>
        </div>
        )}
      <Background
        imageUrl={getBackgroundPicture(card.computedValues, vertical)}
        useBackground
        styleOverride={{
          bgContent: 'bacon-cards-twobythree__bg-content',
          bgWrapper: 'bacon-cards-twobythree__bg-wrapper',
          bgGradient: hedAndDekAreEmpty
            ? false
            : ['bacon-cards-twobythree__gradient', 'bacon-cards-twobythree__additional-gradient'],
        }}
        data-test="background"
      >
        {!noDek
          && (
            <CardOverlay
              additionalClasses={{ container: cardOverlayContainer }}
              activeOnHover
              data-test="cardOverlay"
            >
              {description(dek)}
              {button(url, vertical)}
            </CardOverlay>
          )}
      </Background>
    </div>
  );
};

TwoByThree.contextTypes = {
  vertical: PropTypes.string,
};

TwoByThree.propTypes = {
  card: contentPropType,
};

TwoByThree.defaultProps = {
  card: {},
};

export default TwoByThree;
